import { Alert, Button, DateSelect, Select } from '@sixfold/common-ui';
import { exhaustiveCheck } from '@sixfold/typed-primitives';
import { DateTime } from 'luxon';
import React, { useReducer } from 'react';

import { TrafficState, TrafficInputAction, TrafficAction } from './types';
import { TrafficApiName } from '../../../lib/graphql';

interface Props {
  trafficState: TrafficState;
  trafficDispatcher: React.Dispatch<TrafficAction>;
  loading: boolean;
}
function inputReducer(state: TrafficState, action: TrafficInputAction): TrafficState {
  switch (action.type) {
    case 'setApiOperation':
      return { ...state, apiOperation: action.value };
    case 'setFromDate':
      return { ...state, fromDate: action.value };
    case 'setToDate':
      return { ...state, toDate: action.value };
  }
  exhaustiveCheck(action['type']);
}

export const TourTrafficQueryInput: React.FunctionComponent<Props> = ({
  trafficState,
  trafficDispatcher,
  loading,
}: Props) => {
  const [inputState, inputDispatcher] = useReducer(inputReducer, trafficState);
  const tzAbbreviation = DateTime.now().toFormat('ZZZZ');

  const options: { label: string; value: TrafficApiName }[] = [
    { label: 'PlatformAPI.CREATE_OR_UPDATE_TRANSPORT', value: TrafficApiName.CREATE_OR_UPDATE_TRANSPORT },
    { label: 'PlatformAPI.DELETE_TRANSPORT', value: TrafficApiName.DELETE_TRANSPORT },
    { label: 'PlatformAPI.FORWARD_TRANSPORT', value: TrafficApiName.FORWARD_TRANSPORT },
    { label: 'PlatformAPI.STATUS_UPDATE_TRANSPORT', value: TrafficApiName.STATUS_UPDATE_TRANSPORT },
    {
      label: 'PlatformAPI.CREATE_OR_UPDATE_VEHICLE_ALLOCATION',
      value: TrafficApiName.CREATE_OR_UPDATE_VEHICLE_ALLOCATION,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '1em' }}>
        <label>API operation</label>
        <Select
          label="API operation"
          labelPosition="hidden"
          options={options}
          value={options.find((option) => option.value === inputState.apiOperation) ?? null}
          onChange={(option) => {
            const value = option?.value;
            if (value !== undefined) {
              inputDispatcher({ type: 'setApiOperation', value: value as TrafficApiName });
            }
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: '1em', marginBottom: '1em' }}>
        <div style={{ flex: 1 }}>
          <DateSelect
            label={`From (start of day, ${tzAbbreviation})`}
            name="fromDate"
            format="dd.MM.yyyy"
            value={inputState.fromDate.toJSDate()}
            toDate={DateTime.now().toJSDate()}
            onChange={(date) => {
              if (date !== null) {
                inputDispatcher({ type: 'setFromDate', value: DateTime.fromJSDate(date).startOf('day') });
              }
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <DateSelect
            label={`To (end of day, ${tzAbbreviation})`}
            name="toDate"
            format="dd.MM.yyyy"
            value={inputState.toDate.toJSDate()}
            fromDate={inputState.fromDate.toJSDate()}
            toDate={DateTime.now().toJSDate()}
            onChange={(date) => {
              if (date !== null) {
                inputDispatcher({ type: 'setToDate', value: DateTime.fromJSDate(date).endOf('day') });
              }
            }}
          />
        </div>
      </div>
      {inputState.fromDate > inputState.toDate && (
        <div style={{ marginBottom: '1em' }}>
          <Alert kind="warning">&apos;From&apos; date must be before &apos;To&apos; date!</Alert>
        </div>
      )}
      <Button
        kind="primary"
        onClick={() => {
          trafficDispatcher({
            type: 'setInput',
            value: { ...inputState, loading: true, shouldSkipQuery: false },
          });
        }}
        disabled={loading || inputState.fromDate > inputState.toDate}>
        Fetch API traffic
      </Button>
    </div>
  );
};
